import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

import { api, ApiError, PAGE_SIZE } from "@/api/client";
import type { Paper, PaperStatus, QueueStatus } from "@/api/types";
import { Dialog, ErrorState, LoadingState, useToast } from "@/components/ui";
import { ListenModeSelect, type ListenCommand } from "@/components/audio-player";
import { PaperCard, PaperDetail } from "@/components/paper-card";
import { SwipeDeck, type SwipeAction } from "@/components/swipe-deck";
import { isNativeInteractionTarget, VoiceCommand } from "@/components/voice-command";
import { deleteAction, enqueueAction, flushActions, isOfflineFailure, makePendingAction } from "@/lib/actionQueue";
import { prefetchAudioWindow } from "@/lib/audioPrefetch";
import {
  audioStageForPaper,
  isListenPurpose,
  listenModeStorageKey,
  normalizeListenMode,
  resolveVoiceIntent,
  type ListenMode,
  type ListenPurpose,
  type ListenStage,
} from "@/lib/audioPolicy";
import { createId } from "@/lib/id";
import { useScreenWakeLock } from "@/lib/screenWakeLock";
import { readMinRelevanceScore } from "@/lib/uiPreferences";

const STATUS_LABEL: Record<QueueStatus, string> = {
  new: "未处理",
  review: "复筛",
  deep_read: "精读",
  keep: "保留",
  later: "稍后",
  archive: "归档",
  todo: "待办",
};

const PRIMARY_STATUSES: QueueStatus[] = ["new", "review", "deep_read"];
const MORE_STATUSES: QueueStatus[] = ["keep", "later", "archive", "todo"];

export function QueueNavigation({
  status,
  onNavigate,
}: {
  status: QueueStatus;
  onNavigate: (status: QueueStatus) => void;
}) {
  const more = useRef<HTMLDetailsElement>(null);
  const select = (next: QueueStatus): void => {
    if (more.current) more.current.open = false;
    onNavigate(next);
  };
  useEffect(() => {
    const dismiss = (event: PointerEvent): void => {
      if (more.current?.open && event.target instanceof Node && !more.current.contains(event.target)) more.current.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && more.current?.open) {
        more.current.open = false;
        more.current.querySelector<HTMLElement>("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);
  return (
    <nav class="tabs queue-tabs" aria-label="论文状态">
      {PRIMARY_STATUSES.map((item) => (
        <button type="button" key={item} class={item === status ? "active" : ""} aria-current={item === status ? "page" : undefined} onClick={() => select(item)}>
          {STATUS_LABEL[item]}
        </button>
      ))}
      <details class="queue-more" ref={more}>
        <summary aria-label="更多论文状态">更多</summary>
        <div class="queue-more-menu" role="group" aria-label="其他论文状态">
          {MORE_STATUSES.map((item) => (
            <button type="button" key={item} class={item === status ? "active" : ""} aria-current={item === status ? "page" : undefined} onClick={() => select(item)}>
              {STATUS_LABEL[item]}
            </button>
          ))}
        </div>
      </details>
    </nav>
  );
}

export function papersForRound(papers: Paper[], skipped: ReadonlySet<number>, status: QueueStatus): Paper[] {
  return status === "new" || status === "review" ? papers.filter((paper) => !skipped.has(paper.id)) : papers;
}

export function queueProgress(remaining: number, processed: number): { processed: number; total: number } {
  return { processed, total: processed + remaining };
}

export type QueueKeyboardAction = PaperStatus | "skip";

export function keyboardActionFor(status: QueueStatus, key: string): QueueKeyboardAction | null {
  if (status !== "new" && status !== "review") return null;
  const normalized = key.length === 1 ? key.toLowerCase() : key;
  if (normalized === "h" || normalized === "ArrowLeft") return "archive";
  if (normalized === "k" || normalized === "ArrowUp") return "keep";
  if (normalized === "l" || normalized === "ArrowRight") return status === "new" ? "review" : "deep_read";
  if (normalized === "j" || normalized === "ArrowDown") return "skip";
  return null;
}

type QueueHistoryEntry = {
  id: string;
  paper: Paper;
  skip?: boolean;
  offlineKey?: string;
};

export function safePaperOpenLink(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "zotero:" || url.username || url.password) return null;
    if (!new Set(["select", "open-pdf"]).has(url.hostname)) return null;
    if (!/^\/library\/items\/[A-Za-z0-9]+(?:\/|$)/.test(url.pathname)) return null;
    return url.href;
  } catch {
    return null;
  }
}

function initialListenModes(): Record<ListenStage, ListenMode> {
  return {
    new: normalizeListenMode(window.localStorage.getItem(listenModeStorageKey("new")), "new"),
    review: normalizeListenMode(window.localStorage.getItem(listenModeStorageKey("review")), "review"),
  };
}

export function QueueView({
  status,
  user,
  onNavigate,
}: {
  status: QueueStatus;
  user: string;
  onNavigate: (status: QueueStatus) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [minScore] = useState(readMinRelevanceScore);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Paper | null>(null);
  const [actionHistory, setActionHistory] = useState<QueueHistoryEntry[]>([]);
  const [skippedIds, setSkippedIds] = useState<Set<number>>(() => new Set());
  const [listeningEnabled, setListeningEnabled] = useState(false);
  const [listenModes, setListenModes] = useState<Record<ListenStage, ListenMode>>(initialListenModes);
  const [listenCommand, setListenCommand] = useState<ListenCommand | null>(null);
  const voiceMenu = useRef<HTMLDetailsElement>(null);

  const papersQuery = useInfiniteQuery({
    queryKey: ["papers", status, minScore],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => api.papers({
      status,
      cursor: pageParam,
      minScore,
      limit: PAGE_SIZE,
      signal,
    }),
    getNextPageParam: (page) => page.next_cursor ?? undefined,
    staleTime: 20_000,
  });

  const allPapers = useMemo(
    () => papersQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [papersQuery.data],
  );
  const papers = useMemo(
    () => papersForRound(allPapers, skippedIds, status),
    [allPapers, skippedIds, status],
  );
  const swipeStage = status === "new" || status === "review";
  const toolbarListenStage: ListenStage = status === "review" || status === "deep_read" ? "review" : "new";
  const lastAction = actionHistory.at(-1) ?? null;
  const progress = queueProgress(papers.length, actionHistory.length);

  useScreenWakeLock(listeningEnabled);

  useEffect(() => {
    setSkippedIds(new Set());
    setActionHistory([]);
  }, [status, minScore]);

  useEffect(() => {
    const dismiss = (event: PointerEvent): void => {
      if (!(event.target instanceof Node)) return;
      for (const menu of [voiceMenu.current]) {
        if (menu?.open && !menu.contains(event.target)) menu.open = false;
      }
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      const menu = [voiceMenu.current].find((item) => item?.open);
      if (menu) {
        menu.open = false;
        menu.querySelector<HTMLElement>("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    if (
      swipeStage
      && papers.length <= 5
      && papersQuery.hasNextPage
      && !papersQuery.isFetchingNextPage
    ) void papersQuery.fetchNextPage();
  }, [papers.length, papersQuery.fetchNextPage, papersQuery.hasNextPage, papersQuery.isFetchingNextPage, swipeStage]);

  useEffect(() => {
    if (!listeningEnabled || !papers.length) return;
    let cancelled = false;
    let timer: number | null = null;
    const run = async (attempt: number): Promise<void> => {
      const result = await prefetchAudioWindow({
        enabled: true,
        papers,
        modes: listenModes,
      });
      if (!cancelled && result.pending > 0) {
        const delay = Math.min(30_000, 3_000 * (attempt + 1));
        timer = window.setTimeout(() => { void run(attempt + 1); }, delay);
      }
    };
    void run(0);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [listeningEnabled, listenModes, papers]);

  useEffect(() => {
    const flush = (): void => {
      void flushActions(user).then((count) => {
        if (count) {
          toast(`已补交 ${count} 个离线决定`, "success");
          void queryClient.invalidateQueries({ queryKey: ["papers"] });
        }
      });
    };
    window.addEventListener("online", flush);
    flush();
    return () => window.removeEventListener("online", flush);
  }, [queryClient, toast, user]);

  const removePaper = (paperId: number): void => {
    queryClient.setQueriesData<{ pages: Array<{ items: Paper[] }>; pageParams: unknown[] }>(
      { queryKey: ["papers", status] },
      (current) => current ? {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          items: page.items.filter((paper) => paper.id !== paperId),
        })),
      } : current,
    );
  };

  const reinsertPaper = (paper: Paper): void => {
    queryClient.setQueriesData<{ pages: Array<{ items: Paper[] }>; pageParams: unknown[] }>(
      { queryKey: ["papers", status] },
      (current) => current ? {
        ...current,
        pages: current.pages.map((page, index) => index === 0 && !page.items.some((item) => item.id === paper.id)
          ? { ...page, items: [paper, ...page.items] }
          : page),
      } : current,
    );
  };

  const updateListenMode = (stage: ListenStage, mode: ListenMode): void => {
    window.localStorage.setItem(listenModeStorageKey(stage), mode);
    setListenModes((current) => ({ ...current, [stage]: mode }));
  };

  const startContinuousListening = (purpose: ListenPurpose): void => {
    setListeningEnabled(true);
    setListenCommand({ id: createId("listen"), purpose });
  };

  const toggleContinuousListening = (): void => {
    if (listeningEnabled) {
      document.querySelector<HTMLAudioElement>(".paper-list audio, .swipe-view audio")?.pause();
      setListeningEnabled(false);
      return;
    }
    const current = papers[0];
    if (!current) return;
    const stage = audioStageForPaper(current);
    startContinuousListening(listenModes[stage]);
  };

  const listenCurrentPaper = (): void => {
    const current = papers[0];
    if (!current) return;
    const stage = audioStageForPaper(current);
    setListenCommand({ id: createId("listen-current"), purpose: listenModes[stage] });
  };

  const skipPaper = (paper: Paper): void => {
    if (!swipeStage) return;
    setSkippedIds((current) => new Set(current).add(paper.id));
    setActionHistory((current) => [...current, { id: createId("skip"), paper, skip: true }]);
    setDetail((current) => current?.id === paper.id ? null : current);
    toast("已跳过，本轮不会再次出现");
  };

  const changeStatus = async (paper: Paper, nextStatus: PaperStatus): Promise<boolean> => {
    const actionId = createId("decision");
    setBusyId(paper.id);
    try {
      await api.updateStatus(paper, nextStatus, actionId);
      removePaper(paper.id);
      setActionHistory((current) => [...current, { id: actionId, paper }]);
      toast(`已标记为${STATUS_LABEL[nextStatus]}`, "success");
      return true;
    } catch (error) {
      if (isOfflineFailure(error)) {
        const pending = makePendingAction(user, paper, nextStatus, actionId);
        await enqueueAction(pending);
        removePaper(paper.id);
        setActionHistory((current) => [...current, { id: actionId, paper, offlineKey: pending.key }]);
        toast("已保存到本机，联网后自动提交");
        return true;
      } else if (error instanceof ApiError && error.status === 409) {
        toast("论文已在其他设备更新，正在刷新", "error");
        await papersQuery.refetch();
      } else {
        toast(error instanceof Error ? error.message : "保存失败", "error");
      }
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (paper: Paper): Promise<void> => {
    setBusyId(paper.id);
    try {
      await api.restore(paper, createId("restore"));
      removePaper(paper.id);
      toast("已恢复到筛选队列", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "恢复失败", "error");
    } finally { setBusyId(null); }
  };

  const setRead = async (paper: Paper): Promise<void> => {
    setBusyId(paper.id);
    try {
      await api.setRead(paper, !paper.read_at);
      await papersQuery.refetch();
      toast(paper.read_at ? "已移回精读队列" : "已标记读完", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "保存失败", "error");
    } finally { setBusyId(null); }
  };

  const retryAnnotations = async (paper: Paper): Promise<void> => {
    setBusyId(paper.id);
    try {
      await api.retryAnnotations(paper.id);
      toast("候选批注已重新排队", "success");
      await papersQuery.refetch();
    } catch (error) {
      toast(error instanceof Error ? error.message : "批注重试失败", "error");
    } finally { setBusyId(null); }
  };

  const openPaper = async (paper: Paper): Promise<void> => {
    try {
      const result = await api.openPaper(paper.id);
      const link = safePaperOpenLink(result.link);
      if (link) window.location.assign(link);
      else toast(result.link ? "服务器返回了不安全的 Zotero 链接，已拒绝打开" : "尚未找到可打开的 Zotero 条目", "error");
    } catch (error) {
      toast(error instanceof Error ? error.message : "无法打开 Zotero", "error");
    }
  };

  const undo = async (): Promise<void> => {
    if (!lastAction) return;
    try {
      if (lastAction.skip) {
        setSkippedIds((current) => {
          const next = new Set(current);
          next.delete(lastAction.paper.id);
          return next;
        });
      } else if (lastAction.offlineKey) {
        await deleteAction(lastAction.offlineKey);
        reinsertPaper(lastAction.paper);
      } else {
        await api.undo(lastAction.id);
      }
      setActionHistory((current) => current.slice(0, -1));
      if (!lastAction.skip && navigator.onLine) await papersQuery.refetch();
      toast("已撤销", "success");
    } catch (error) {
      toast(error instanceof Error ? error.message : "撤销失败", "error");
    }
  };

  const runVoiceIntent = async (input: string): Promise<void> => {
    const text = input.trim();
    if (!text) return;
    try {
      const resolved = await resolveVoiceIntent(text, api.voiceIntent);
      if (resolved.source === "ambiguous") {
        toast("指令同时包含听读和论文操作，已安全忽略", "error");
        return;
      }
      const current = papers[0];
      if (!current) {
        toast("当前队列没有可操作论文", "error");
        return;
      }
      if (isListenPurpose(resolved.intent)) {
        const stage = audioStageForPaper(current);
        if (resolved.intent !== "abstract") updateListenMode(stage, resolved.intent);
        startContinuousListening(resolved.intent);
        return;
      }
      if (resolved.intent === "skip") {
        if (swipeStage) skipPaper(current);
        else toast("仅刷卡队列支持跳过本轮", "error");
      } else if (resolved.intent === "undo") {
        await undo();
      } else if (resolved.intent === "replay") {
        const stage = audioStageForPaper(current);
        startContinuousListening(listenModes[stage]);
      } else if (resolved.intent === "pause") {
        document.querySelector<HTMLAudioElement>(".paper-list audio")?.pause();
        setListeningEnabled(false);
        toast("听读已暂停");
      } else if (["keep", "review", "deep_read", "later", "archive"].includes(resolved.intent)) {
        let target = resolved.intent as PaperStatus;
        if (target === "deep_read" && current.status === "new") target = "review";
        const allowed = current.status === "new"
          ? new Set<PaperStatus>(["keep", "review", "later", "archive"])
          : new Set<PaperStatus>(["keep", "deep_read", "later", "archive"]);
        if (allowed.has(target)) await changeStatus(current, target);
        else toast("该指令不适用于当前论文阶段", "error");
      } else {
        toast("没有识别到可执行指令", "error");
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : "指令识别失败", "error");
    }
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (
        event.defaultPrevented
        || event.repeat
        || event.altKey
        || event.ctrlKey
        || event.metaKey
        || isNativeInteractionTarget(event.target)
        || document.querySelector("dialog[open]") !== null
      ) return;
      const action = keyboardActionFor(status, event.key);
      const current = papers[0];
      if (!action || !current || busyId !== null) return;
      event.preventDefault();
      if (action === "skip") skipPaper(current);
      else void changeStatus(current, action);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  const commitSwipeAction = (paper: Paper, action: SwipeAction): boolean | Promise<boolean> => {
    if (action === "skip") {
      skipPaper(paper);
      return true;
    }
    return changeStatus(paper, action);
  };

  return (
    <>
      <div class={`queue-screen${swipeStage ? " swipe-queue-screen" : ""}`}>
        <section class="queue-toolbar" aria-label="论文队列筛选">
          <QueueNavigation status={status} onNavigate={onNavigate} />
          <div class="queue-control-row">
            <p class="mobile-queue-count" aria-label={`本批进度 ${progress.processed}/${progress.total}`} aria-live="polite">{progress.processed}/{progress.total}</p>
            <div class="queue-tools">
              <ListenModeSelect
                label="播报模式"
                value={listenModes[toolbarListenStage]}
                onChange={(mode) => updateListenMode(toolbarListenStage, mode)}
              />
              <button type="button" class="btn btn-ghost listen-current" disabled={!papers.length} onClick={listenCurrentPaper}>听当前</button>
              <button
                type="button"
                class="btn btn-ghost voice-toggle"
                aria-pressed={listeningEnabled}
                onClick={toggleContinuousListening}
              >
                {listeningEnabled ? "停止听读" : "连续听读"}
              </button>
              {lastAction && <button type="button" class="btn btn-ghost contextual-action" onClick={() => void undo()}>撤销</button>}
              {swipeStage && skippedIds.size > 0 && (
                <button type="button" class="btn btn-ghost contextual-action" onClick={() => setSkippedIds(new Set())}>新一轮（{skippedIds.size}）</button>
              )}
              <details class="queue-voice" ref={voiceMenu}>
                <summary aria-label="语音指令"><span class="voice-summary-icon" aria-hidden="true">🎙</span><span class="voice-summary-label">语音指令</span></summary>
                <div class="queue-voice-panel"><VoiceCommand enabled={papers.length > 0} onIntent={runVoiceIntent} /></div>
              </details>
            </div>
          </div>
        </section>

        <section class={`queue-heading${swipeStage ? " swipe-heading" : ""}`}>
          <div class="queue-heading-copy">
            <h1>{STATUS_LABEL[status]}</h1>
            <p aria-live="polite">进度 {progress.processed}/{progress.total}{papersQuery.hasNextPage ? " · 后续自动加载" : ""}</p>
          </div>
          {swipeStage && (
            <>
              <p id="swipe-instructions" class="gesture-guide">
                <span>← 归档</span><span>→ {status === "new" ? "复筛" : "精读"}</span><span>↑ 保留</span><span>↓ 跳过</span>
              </p>
              <p class="keyboard-hint sr-only">键盘：H/← 归档 · K/↑ 保留 · L/→ {status === "new" ? "复筛" : "精读"} · J/↓ 跳过本轮 · 按住空格说话</p>
            </>
          )}
        </section>

      {papersQuery.isPending && <LoadingState label="正在加载论文队列…" />}
      {papersQuery.isError && <ErrorState error={papersQuery.error} retry={() => void papersQuery.refetch()} />}
      {!papersQuery.isPending && !papersQuery.isError && !papers.length && !papersQuery.hasNextPage && (
        <div class="state-panel"><strong>队列为空</strong><span>这里暂时没有论文。</span></div>
      )}
      {swipeStage && papers[0] ? (() => {
        const paper = papers[0];
        const stage = audioStageForPaper(paper);
        return (
          <main class="swipe-view" aria-busy={papersQuery.isFetching} aria-describedby="swipe-instructions">
            <div class="swipe-stack">
              <SwipeDeck
                key={paper.id}
                status={status}
                busy={busyId === paper.id}
                onAction={(action) => commitSwipeAction(paper, action)}
              >
                {(commit) => (
                  <PaperCard
                    paper={paper}
                    busy={busyId === paper.id}
                    variant="swipe"
                    onStatus={(_current, next) => commit(next as SwipeAction)}
                    onRestore={(current) => void restore(current)}
                    onDetail={setDetail}
                    onRead={(current) => void setRead(current)}
                    onOpen={(current) => void openPaper(current)}
                    onSkip={() => commit("skip")}
                    onRetryAnnotations={(current) => void retryAnnotations(current)}
                    listenMode={listenModes[stage]}
                    listenCommand={listenCommand ?? undefined}
                  />
                )}
              </SwipeDeck>
            </div>
          </main>
        );
      })() : !swipeStage ? (
        <main class="paper-list" aria-busy={papersQuery.isFetching}>
          {papers.map((paper, index) => {
            const stage = audioStageForPaper(paper);
            return (
              <PaperCard
                key={paper.id}
                paper={paper}
                busy={busyId === paper.id}
                onStatus={(current, next) => void changeStatus(current, next)}
                onRestore={(current) => void restore(current)}
                onDetail={setDetail}
                onRead={(current) => void setRead(current)}
                onOpen={(current) => void openPaper(current)}
                onRetryAnnotations={(current) => void retryAnnotations(current)}
                listenMode={listenModes[stage]}
                listenCommand={index === 0 ? listenCommand ?? undefined : undefined}
              />
            );
          })}
        </main>
      ) : null}
      {!swipeStage && papersQuery.hasNextPage && (
        <div class="load-more"><button type="button" class="btn btn-ghost" disabled={papersQuery.isFetchingNextPage} onClick={() => void papersQuery.fetchNextPage()}>{papersQuery.isFetchingNextPage ? "加载中…" : "加载更多"}</button></div>
      )}
      </div>
      <Dialog title={detail?.title ?? "论文详情"} open={detail !== null} onClose={() => setDetail(null)} wide>
        {detail && <PaperDetail paper={detail} />}
      </Dialog>
    </>
  );
}
